import {
  Box,
  Button,
  Chip,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

/** A schedule entry the user can still change. Entries already sent to the
 *  client come back from the API and are shown read-only beside these. */
export interface ServiceReminderScheduleValue {
  is_active: boolean;
  interval_months: string;
  /** How many reminders the "every N months" helper lays out, already-sent
   *  ones included. Form-only: the reminder rows are what gets saved. */
  occurrences: string;
  lead_time_days: string;
  auto_create_job: boolean;
  /** Pending dates, ISO "YYYY-MM-DD", always kept sorted. */
  dates: string[];
}

export interface SentServiceReminder {
  id?: number;
  due_date: string;
  status_display?: string;
  job_number?: string | null;
}

interface ServiceReminderScheduleProps {
  value: ServiceReminderScheduleValue;
  onChange: (value: ServiceReminderScheduleValue) => void;
  /** The sale date the "every N months" helper counts from. */
  anchorDate?: string;
  /** Reminders already sent or completed - history, not editable here. */
  sentReminders?: SentServiceReminder[];
  disabled?: boolean;
}

export function createEmptyServiceReminderSchedule(): ServiceReminderScheduleValue {
  return {
    is_active: true,
    interval_months: "12",
    occurrences: "3",
    lead_time_days: "14",
    auto_create_job: false,
    dates: [],
  };
}

/** Dates every `intervalMonths` from `anchor`, `count` of them, starting one
 *  interval after the sale - or `skip` intervals further on, to continue a
 *  series part of which is already behind it. Mirrors the backend's
 *  `build_reminder_dates`, so what the form previews is what the schedule
 *  becomes. */
export function buildIntervalDates(
  anchor: Dayjs,
  intervalMonths: number,
  count: number,
  skip = 0
): string[] {
  return Array.from({ length: count }, (_, step) =>
    anchor.add(intervalMonths * (skip + step + 1), "month").format("YYYY-MM-DD")
  );
}

/** How many slots of the series reminders already sent have used up: at
 *  least one each, and every slot up to the last one sent, so the series
 *  picks up after its history rather than inside it - also when those dates
 *  were moved, or the interval has changed since they were laid out. */
function countUsedSlots(
  anchor: Dayjs,
  intervalMonths: number,
  sentReminders: SentServiceReminder[]
): number {
  if (sentReminders.length === 0) return 0;

  const lastSentDate = sentReminders
    .map((reminder) => reminder.due_date)
    .reduce((latest, date) => (date > latest ? date : latest));

  let used = sentReminders.length;
  while (buildIntervalDates(anchor, intervalMonths, 1, used)[0] <= lastSentDate) {
    used += 1;
  }

  return used;
}

/** The pending dates an "every N months, `count` times" series leaves to be
 *  sent. Reminders already sent count towards `count` and the new dates
 *  continue after them, so stretching a 3-reminder guarantee with 2 already
 *  sent to 5 leaves 3 pending - the 1 still waiting plus 2 more - not 5. */
export function buildPendingDates(
  anchor: Dayjs,
  intervalMonths: number,
  count: number,
  sentReminders: SentServiceReminder[] = []
): string[] {
  const remaining = Math.max(count - sentReminders.length, 0);
  const usedSlots = countUsedSlots(anchor, intervalMonths, sentReminders);

  return sortDates(buildIntervalDates(anchor, intervalMonths, remaining, usedSlots));
}

function sortDates(dates: string[]): string[] {
  return Array.from(new Set(dates)).sort();
}

/**
 * Picks the dates a sold machine has to be serviced on to keep its guarantee.
 *
 * Dates are always concrete, never a rule: "every 12 months" is a button that
 * fills the list in, and every date it produces can then be removed one at a
 * time. Dates are only ever laid out from the interval - there is no picking
 * a single date by hand here.
 */
export default function ServiceReminderSchedule({
  value,
  onChange,
  anchorDate,
  sentReminders = [],
  disabled = false,
}: ServiceReminderScheduleProps) {
  const { t } = useTranslation();

  const update = (patch: Partial<ServiceReminderScheduleValue>) =>
    onChange({ ...value, ...patch });

  const handleRemoveDate = (date: string) =>
    update({ dates: value.dates.filter((existing) => existing !== date) });

  const handleGenerate = () => {
    const interval = parseInt(value.interval_months, 10);
    const count = parseInt(value.occurrences, 10);
    if (!interval || !count || !anchorDate) return;

    // Replaces the pending dates rather than adding to them: the interval is
    // the only way dates get here, so regenerating after changing it should
    // leave one schedule, not two interleaved ones. Already-sent reminders
    // live outside `dates` and are untouched.
    update({
      dates: buildPendingDates(dayjs(anchorDate), interval, count, sentReminders),
    });
  };

  return (
    <Box
      sx={{
        border: "1px solid #ccc",
        borderRadius: 2,
        p: 2,
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
            {t("Obligatory service reminders")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("Active")}
            </Typography>
            <Switch
              size="small"
              checked={value.is_active}
              disabled={disabled}
              onChange={(_, checked) => update({ is_active: checked })}
            />
          </Box>
        </Box>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          {t(
            "The client is emailed ahead of each date so the service that keeps their guarantee actually happens."
          )}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" }, alignItems: "center" }}>
        <TextField
          size="small"
          label={t("Remind this many days ahead")}
          value={value.lead_time_days}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            if (next !== "" && !/^\d{0,3}$/.test(next)) return;
            update({ lead_time_days: next });
          }}
          inputProps={{ inputMode: "numeric" }}
          sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: { xs: "1 1 100%", sm: 1 },
          }}
        >
          <Switch
            size="small"
            checked={value.auto_create_job}
            disabled={disabled}
            onChange={(_, checked) => update({ auto_create_job: checked })}
          />
          <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
            {t("Create a service job automatically")}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" }, alignItems: "center" }}>
        <TextField
          size="small"
          label={t("Every (months)")}
          value={value.interval_months}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            if (next !== "" && !/^\d{0,3}$/.test(next)) return;
            update({ interval_months: next });
          }}
          inputProps={{ inputMode: "numeric" }}
          sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
        />
        <TextField
          size="small"
          label={t("How many times")}
          value={value.occurrences}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            if (next !== "" && !/^\d{0,2}$/.test(next)) return;
            update({ occurrences: next });
          }}
          inputProps={{ inputMode: "numeric" }}
          sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
        />
        <Tooltip
          title={
            anchorDate
              ? t("Fills the list from the contract start date")
              : t("Set the contract start date first")
          }
        >
          <Box component="span" sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
            <Button
              variant="outlined"
              disabled={disabled || !anchorDate}
              onClick={handleGenerate}
              fullWidth
            >
              {t("Generate dates")}
            </Button>
          </Box>
        </Tooltip>
      </Box>
      {sentReminders.length > 0 && (
        <Typography variant="caption" sx={{ color: "text.secondary", mt: -1 }}>
          {t(
            "The count includes the reminders already sent ({{count}}) - new dates continue after them.",
            { count: sentReminders.length }
          )}
        </Typography>
      )}

      {value.dates.length === 0 ? (
        // An active plan needs at least one date to be saved - said here, next
        // to the button that fixes it, rather than only on submit.
        value.is_active && sentReminders.length === 0 ? (
          <Typography variant="caption" sx={{ color: "error.main" }}>
            {t("Active service reminders need at least one date - generate the dates or switch the reminders off.")}
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("No service dates yet.")}
          </Typography>
        )
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {value.dates.map((date) => (
            <Chip
              key={date}
              label={date}
              size="small"
              onDelete={disabled ? undefined : () => handleRemoveDate(date)}
              deleteIcon={<Delete fontSize="small" />}
            />
          ))}
        </Stack>
      )}

      {sentReminders.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
            {t("Already sent - kept for the record and not editable here.")}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {sentReminders.map((reminder) => (
              <Chip
                key={reminder.id ?? reminder.due_date}
                size="small"
                variant="outlined"
                label={
                  reminder.status_display
                    ? `${reminder.due_date} — ${reminder.status_display}`
                    : reminder.due_date
                }
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
